var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { isNumber } from '@design-stack-ct/utility-core';
import { getUploads } from '../services/UploadsMetadataService';
import { AssetType } from './types';
import { AssetStatusType, AssetStore, AssetStoreFetchStatusType, createFolder, getImageInfoByClassification, generateImagePreview, shouldGenerateImagePreview, AssetStoreEvent, parseAssetUrl, isFetchSingleAssetByMetadataParams, isFetchSingleAssetByUrlParams, } from '@design-stack-ct/assets-sdk';
import { VistaAsset } from './VistaAsset';
import { FeaturesClient } from '@vp/ft-reader';
import { action, computed, makeObservable, observable } from 'mobx';
import { SHERBERT_BASE_URL, UPLOADS_BASE_URL } from '../utilities/constants';
import { fetchAssetIdFromSherbertUrl } from '../utilities/common';
const MAX_POLLING_COUNT = 600;
const POLLING_INTERVAL_MS = 50;
/**
 * Used to access Vista Assets from either the Sherbert DAM or Uploads V1
 */
export class VistaAssetStore {
    /**
     * Creates a new VistaAssetStore with the provided parameters.
     * @param {VistaAssetStoreParams} ParametersObject Parameters object for this constructor. Contains authProvider, subBrand, experience, and tenant. See type documentation for more information.
     */
    constructor({ authProvider, subBrand, experience, writeTenant, readTenant, callbackFunc }) {
        Object.defineProperty(this, "authProvider", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "writeTenant", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "readTenant", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "useDamRead", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "featureFlagChecked", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "assetStore", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "subBrand", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "experience", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "vistaTenant", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: '4HVsAccqLzE6BPbmvrDaKw'
        });
        Object.defineProperty(this, "callbackFunc", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "previousFetchParams", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_assets", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "_fetchStatus", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: { type: AssetStoreFetchStatusType.Idle }
        });
        makeObservable(this);
        this.authProvider = authProvider;
        this.subBrand = subBrand;
        this.experience = experience;
        this.writeTenant = writeTenant;
        this.readTenant = readTenant || this.vistaTenant;
        this.callbackFunc = callbackFunc;
        this.assetStore = new AssetStore({ authProvider: this.authProvider, tenant: readTenant || this.vistaTenant });
    }
    get assets() {
        return this._assets.filter((asset) => asset.status.type !== AssetStatusType.Deleted);
    }
    get fetchStatus() {
        return this.useDamRead ? this.assetStore.fetchStatus : this._fetchStatus;
    }
    get canFetchMore() {
        var _a;
        return !!((_a = this.previousFetchParams) === null || _a === void 0 ? void 0 : _a.offset);
    }
    /**
     * Sets DAM related flags based on feature flags. Called as part of service calls in the SDK.
     */
    setup() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.featureFlagChecked) {
                yield FeaturesClient.getFeaturesClient('vista-assets-sdk').then((res) => {
                    this.useDamRead = res.isFeatureEnabled('vista_assets_sdk_use_sherbert_read');
                    this.featureFlagChecked = true;
                });
            }
        });
    }
    /**
     * Method used to fetch Metadata of a sherbert asset using assetId
     * @param FetchSingleAssetParams sherbert assetId
     * @returns {VistaAsset} Returns an AssetData.
     */
    fetchSingleAsset(params) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const { signal } = params;
                if (isFetchSingleAssetByMetadataParams(params)) {
                    const { query, signal } = params;
                    const data = yield this.assetStore.fetchSingleAsset({ query, signal: signal });
                    const result = this.assetFromUploadData(data);
                    if (result)
                        return result;
                }
                else {
                    const { id, signature } = isFetchSingleAssetByUrlParams(params) ? parseAssetUrl(params.url) : params;
                    const data = yield this.assetStore.fetchSingleAsset({ id: id, signal: signal, signature: signature });
                    const result = this.assetFromUploadData(data);
                    if (result)
                        return result;
                }
            }
            catch (error) {
                throw new Error(`${error.message}`);
            }
            throw new Error('unable to fetch single asset');
        });
    }
    /**
     * Method used to fetch Metadata of a sherbert asset using URL
     * @param imageUrl sherbert image url
     * @returns {AssetData} Returns an metadata of the asset.
     */
    fetchMetadata({ imageUrl, signal }) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const assetUrl = new URL(imageUrl);
                if (assetUrl.toString().toLowerCase().startsWith(SHERBERT_BASE_URL)) {
                    const assetId = fetchAssetIdFromSherbertUrl(assetUrl.toString());
                    const result = yield this.assetStore.fetchSingleAsset({ id: assetId, signal: signal });
                    if (result.data)
                        return result.data;
                }
                throw new Error('Given URL is invalid sherbert URL');
            }
            catch (error) {
                throw error.message;
            }
        });
    }
    /**
     * creates a subfolder based on parentId.
     * @param {CreateFolderParams} ParametersObject Object holding creteFolder parameters for this method call.
     * @returns {Promise<AssetData>} Returns an AssetData Promise object.Throws an error on createFolder failure .
     */
    createFolder(folderParams) {
        return __awaiter(this, void 0, void 0, function* () {
            const { name, folderId, tenant, signal } = folderParams;
            try {
                const headers = new Headers(yield this.authProvider.getAuthHeaders());
                const response = yield createFolder({ body: { folderId, tenant, name } }, { signal, headers });
                return response.data;
            }
            catch (error) {
                throw error.message;
            }
        });
    }
    /**
     * Fetch a number of assets equal to the limit set in Parameters which are associated with the calling token's id.
     * @param {VistaFetchAssetsParams} ParametersObject Object to pass parameters to this call, limit defaults to 20 and presign defaults to true. Presign true will return presigned asset URLs but will increase the response time of this method.
     * @returns {VistaAsset[]} Returns an array of VistaAsset objects.
     */
    fetchAssets(_a = {}) {
        var { signal, limit = 20, presign = true } = _a, getAssetsParams = __rest(_a, ["signal", "limit", "presign"]);
        return __awaiter(this, void 0, void 0, function* () {
            yield this.setup();
            this.setFetchStatus({ type: AssetStoreFetchStatusType.Fetching });
            this.setAssets([]);
            let assetFetchStatus;
            let assets = [];
            if (this.useDamRead) {
                yield this.assetStore.fetchAssets(Object.assign({ signal, limit, includeSubTenants: true }, getAssetsParams));
                this.assetStore.assets.forEach((a) => {
                    const asset = this.assetFromUploadData(a);
                    if (asset) {
                        assets.push(asset);
                    }
                });
                assetFetchStatus = this.assetStore.fetchStatus;
            }
            else {
                try {
                    assets = yield this._fetchAssets(Object.assign({ signal,
                        limit,
                        presign }, getAssetsParams));
                    assetFetchStatus = { type: AssetStoreFetchStatusType.Idle };
                }
                catch (error) {
                    assetFetchStatus = { type: AssetStoreFetchStatusType.Error, rawError: error };
                }
            }
            this.setAssets(assets);
            this.setFetchStatus(assetFetchStatus);
            return this.assets;
        });
    }
    signAndAddAsset(asset) {
        return __awaiter(this, void 0, void 0, function* () {
            yield asset.presign();
            this.appendAssets(asset);
        });
    }
    setAssets(assets) {
        this._assets = assets;
    }
    appendAssets(...assets) {
        this._assets.push(...assets);
    }
    setFetchStatus(fetchStatus) {
        this._fetchStatus = fetchStatus;
    }
    prependAssets(...assets) {
        this._assets.unshift(...assets);
    }
    setPreviousFetchParams(previousFetchParams) {
        this.previousFetchParams = previousFetchParams;
    }
    /*
     * Paginate assets based on provided parameters.
     * Continues where `fetchAssets` left off, using the same sorting direction.
     */
    fetchMore({ signal, presign, limit } = {}) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.canFetchMore) {
                const newLimit = limit !== null && limit !== void 0 ? limit : (_a = this.previousFetchParams) === null || _a === void 0 ? void 0 : _a.limit;
                const newPresign = presign !== null && presign !== void 0 ? presign : (_b = this.previousFetchParams) === null || _b === void 0 ? void 0 : _b.presign;
                const assets = yield this._fetchAssets(Object.assign(Object.assign({}, this.previousFetchParams), { limit: newLimit, signal, presign: newPresign }));
                // If we dom't presign, we still need to add the assets to the list.
                if (!newPresign) {
                    this.appendAssets(...assets);
                }
            }
        });
    }
    _fetchAssets(_a = {}) {
        var { signal, limit = 20, presign = true } = _a, getAssetsParams = __rest(_a, ["signal", "limit", "presign"]);
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // useDam flag is set to false. Use UMS
                const headers = yield this.authProvider.getAuthHeaders();
                const uploadsResponse = yield getUploads({
                    headers,
                    tenant: getAssetsParams.tenant || this.readTenant,
                    limit: limit,
                    offset: getAssetsParams.offset,
                    signal,
                    includeSubTenants: true,
                    scopeToUserFolders: getAssetsParams.scopeToUserFolders,
                    folderName: getAssetsParams.folderName,
                });
                const assets = uploadsResponse._embedded.item.map((u) => this.assetFromUpload(u));
                const fetchParams = Object.assign(Object.assign(Object.assign({}, (isNumber(limit) && { limit })), { presign, includeSubTenants: true }), getAssetsParams);
                this.setPreviousFetchParams(Object.assign(Object.assign({}, fetchParams), { offset: uploadsResponse.offset }));
                if (presign) {
                    yield Promise.all(assets.map((asset) => this.signAndAddAsset(asset)));
                }
                return assets;
            }
            catch (error) {
                // ignore aborted fetch by user
                if ((error === null || error === void 0 ? void 0 : error.name) !== 'AbortError') {
                    throw error;
                }
                return [];
            }
        });
    }
    // JSDoc comments on this won't show up, only the above two are listed as method variants.
    /**
     * @param options
     * @param callback callback function gets called in case there is temporary imageUrl created. It returns @param UploadCallbackDTO
     * @returns
     */
    upload(options, callback) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.setup();
            if (!options.properties) {
                options.properties = {};
            }
            options.properties.subBrand = this.subBrand;
            options.properties.experience = this.experience;
            options.tenant = options.tenant || this.writeTenant;
            const asset = new VistaAsset({
                id: '',
                account: '',
                createdBy: '',
                createdMs: 0,
                modifiedBy: '',
                modifiedMs: 0,
                expiresOn: '',
                hash: '',
                info: {},
                properties: {},
                tags: [],
                tenant: '',
            }, AssetType.Original, this.authProvider, undefined, this.callbackFunc);
            this.prependAssets(asset);
            if ('file' in options) {
                const onStatusUpdate = (percent) => {
                    const onProgress = options === null || options === void 0 ? void 0 : options.onProgress;
                    if (onProgress) {
                        onProgress(percent);
                    }
                    asset.updateProgress(percent);
                };
                const rawFile = options.file;
                let imageInfo;
                let imageInfoData;
                let imagePreview;
                let uploadCallbackResponse;
                if (rawFile) {
                    try {
                        imageInfo = getImageInfoByClassification(rawFile);
                        if (callback instanceof Function) {
                            if (shouldGenerateImagePreview(rawFile)) {
                                const headers = yield this.authProvider.getAuthHeaders();
                                imagePreview = generateImagePreview(rawFile, headers);
                                imagePreview.then((response) => {
                                    if (response) {
                                        imageInfo.then((info) => {
                                            var _a;
                                            imageInfoData = info;
                                            uploadCallbackResponse = {
                                                imageInfo: info,
                                                tempUploadData: response[0],
                                                tempUploadUrl: `${UPLOADS_BASE_URL}/${(_a = response[0]) === null || _a === void 0 ? void 0 : _a.uploadId}`,
                                                isError: false,
                                            };
                                            callback(uploadCallbackResponse);
                                        });
                                    }
                                });
                            }
                        }
                    }
                    catch (exception) {
                        if (callback) {
                            uploadCallbackResponse = {
                                isError: true,
                            };
                            callback(uploadCallbackResponse);
                        }
                    }
                }
                else {
                    throw Error('Invalid file uploaded');
                }
                yield this.assetStore
                    .upload(Object.assign(Object.assign({}, options), { onProgress: onStatusUpdate }))
                    .then((damAsset) => __awaiter(this, void 0, void 0, function* () {
                    if (damAsset.data) {
                        let pollingCount = MAX_POLLING_COUNT;
                        const timeInterval = setInterval(() => {
                            if (pollingCount <= 0 || imageInfoData) {
                                clearInterval(timeInterval);
                            }
                            if (!imageInfoData) {
                                imageInfo === null || imageInfo === void 0 ? void 0 : imageInfo.then((info) => (imageInfoData = info));
                            }
                            pollingCount -= 1;
                            if (pollingCount === 300) {
                                clearInterval(timeInterval);
                            }
                        }, POLLING_INTERVAL_MS);
                        asset.setAssetData(Object.assign(Object.assign({}, damAsset.data), { info: Object.assign(Object.assign({}, damAsset.data.info), { image: imageInfoData }), isLocallyProcessed: true }));
                        const assetId = damAsset.data.id;
                        pollingCount = MAX_POLLING_COUNT;
                        let assetIndex = -1;
                        this.assetStore.assets.find((assetData, index) => {
                            var _a;
                            if (assetId === ((_a = assetData.data) === null || _a === void 0 ? void 0 : _a.id)) {
                                assetIndex = index;
                                return true;
                            }
                            return false;
                        });
                        if (assetIndex !== -1) {
                            const interval = setInterval(() => {
                                var _a, _b, _c, _d, _e;
                                if (pollingCount <= 0) {
                                    clearInterval(interval);
                                }
                                if (((_a = this.assetStore.assets[assetIndex].data) === null || _a === void 0 ? void 0 : _a.isLocallyProcessed) === false &&
                                    ((_b = this.assetStore.assets[assetIndex].data) === null || _b === void 0 ? void 0 : _b.info.image)) {
                                    if (damAsset.data) {
                                        // Here we are setting image info for Sherbert asset
                                        asset.setAssetData(Object.assign(Object.assign({}, damAsset.data), { info: Object.assign(Object.assign({}, damAsset.data.info), { image: (_c = this.assetStore.assets[assetIndex].data) === null || _c === void 0 ? void 0 : _c.info.image }), isLocallyProcessed: false }));
                                        if (((_e = (_d = this.assetStore.assets[assetIndex].data) === null || _d === void 0 ? void 0 : _d.info.image) === null || _e === void 0 ? void 0 : _e.format) === 'unknown') {
                                            clearInterval(interval);
                                            throw new Error('The format of the URI could not be determined.');
                                        }
                                    }
                                    clearInterval(interval);
                                }
                                pollingCount--;
                            }, POLLING_INTERVAL_MS);
                        }
                    }
                }));
            }
            if ('uri' in options) {
                yield this.assetStore.upload(options).then((damAsset) => __awaiter(this, void 0, void 0, function* () {
                    var _a;
                    if (damAsset.data) {
                        const uriAsset = this.assetDataFromUpload(damAsset.data);
                        asset.setAssetData(uriAsset);
                        yield asset.presign();
                    }
                    const assetId = (_a = damAsset.data) === null || _a === void 0 ? void 0 : _a.id;
                    let pollingCount = MAX_POLLING_COUNT;
                    let assetIndex = -1;
                    this.assetStore.assets.find((assetData, index) => {
                        var _a;
                        if (assetId === ((_a = assetData.data) === null || _a === void 0 ? void 0 : _a.id)) {
                            assetIndex = index;
                            return true;
                        }
                        return false;
                    });
                    if (assetIndex !== -1) {
                        const interval = setInterval(() => {
                            var _a, _b, _c, _d, _e;
                            if (pollingCount <= 0) {
                                clearInterval(interval);
                            }
                            if (((_a = this.assetStore.assets[assetIndex].data) === null || _a === void 0 ? void 0 : _a.isLocallyProcessed) === false &&
                                ((_b = this.assetStore.assets[assetIndex].data) === null || _b === void 0 ? void 0 : _b.info.image)) {
                                if (damAsset.data) {
                                    // Here we are setting image info for Sherbert uri asset
                                    asset.setAssetData(Object.assign(Object.assign({}, damAsset.data), { info: Object.assign(Object.assign({}, damAsset.data.info), { image: (_c = this.assetStore.assets[assetIndex].data) === null || _c === void 0 ? void 0 : _c.info.image }), isLocallyProcessed: false }));
                                    if (((_e = (_d = this.assetStore.assets[assetIndex].data) === null || _d === void 0 ? void 0 : _d.info.image) === null || _e === void 0 ? void 0 : _e.format) === 'unknown') {
                                        clearInterval(interval);
                                        throw new Error('The format of the URI could not be determined.');
                                    }
                                }
                                clearInterval(interval);
                            }
                            pollingCount--;
                        }, POLLING_INTERVAL_MS);
                    }
                }));
            }
            if (asset) {
                yield asset.prepare();
                return asset;
            }
            else {
                throw new Error('Something went wrong while uploading the asset. Please try again.');
            }
        });
    }
    /**
     * Method to subscript to an event of type AssetStoreEvent. Subscriber will get a callback to the lister function provided
     * @param {AssetStoreEvent} type Type of an event to subscribe to
     * @param {EventListenerOrEventListenerObject} listener event listener
     * @param {EventListenerOptions} options event listener options
     * @returns {void} Returns void
     */
    addEventListener(type, listener, options) {
        if (type === AssetStoreEvent.UploadStart ||
            type === AssetStoreEvent.UploadSuccess ||
            type === AssetStoreEvent.UploadError) {
            return this.assetStore.assetStoreEventTarget.addEventListener(type, (event) => this.assetEventToVistaAssetEvent(type, listener, event), options);
        }
        return this.assetStore.assetStoreEventTarget.addEventListener(type, listener, options);
    }
    /**
     * Method to remove the subscription of an event of type AssetStoreEvent. Subscriber will not get the event once the event lister is removed.
     * @param {AssetStoreEvent} type Type of an event to unsubscribe to
     * @param {EventListenerOrEventListenerObject} listener event listener
     * @param {EventListenerOptions} options event listener options
     * @returns {void} Returns void
     */
    removeEventListener(type, listener, options) {
        this.assetStore.assetStoreEventTarget.removeEventListener(type, listener, options);
    }
    assetFromUploadData(upload) {
        if (upload.data) {
            return new VistaAsset(upload.data, AssetType.Original, this.authProvider, undefined, this.callbackFunc);
        }
        else {
            return undefined;
        }
    }
    assetEventToVistaAssetEvent(type, listener, event) {
        const vistaAsset = this.assetFromUploadData(event.detail);
        const vistaAssetEvent = new CustomEvent(type, Object.assign(Object.assign({}, event), { detail: vistaAsset }));
        if (typeof listener === 'function') {
            listener(vistaAssetEvent);
        }
        if (typeof listener === 'object') {
            listener.handleEvent(vistaAssetEvent);
        }
    }
    assetDataFromUpload(upload) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
        return {
            id: upload.id,
            type: upload.type,
            account: upload.account,
            tenant: upload.tenant,
            createdMs: upload.createdMs,
            createdBy: upload.createdBy,
            modifiedMs: upload.modifiedMs,
            modifiedBy: upload.modifiedBy,
            hash: upload.hash,
            expiresOn: upload.expiresOn,
            properties: upload.properties,
            info: {
                asset: {
                    sourceUri: upload.sourceUri,
                    folder: upload.folder || ((_b = (_a = upload === null || upload === void 0 ? void 0 : upload.info) === null || _a === void 0 ? void 0 : _a.asset) === null || _b === void 0 ? void 0 : _b.folder),
                    expires: upload.expires || ((_d = (_c = upload === null || upload === void 0 ? void 0 : upload.info) === null || _c === void 0 ? void 0 : _c.asset) === null || _d === void 0 ? void 0 : _d.expires),
                    folderId: upload.folderId || ((_f = (_e = upload === null || upload === void 0 ? void 0 : upload.info) === null || _e === void 0 ? void 0 : _e.asset) === null || _f === void 0 ? void 0 : _f.folderId),
                    hidden: ((_h = (_g = upload === null || upload === void 0 ? void 0 : upload.info) === null || _g === void 0 ? void 0 : _g.asset) === null || _h === void 0 ? void 0 : _h.hidden) || false,
                },
                storage: {
                    location: (_j = upload === null || upload === void 0 ? void 0 : upload.info) === null || _j === void 0 ? void 0 : _j.storage.location,
                    fileName: (_k = upload === null || upload === void 0 ? void 0 : upload.info) === null || _k === void 0 ? void 0 : _k.storage.fileName,
                    fileSizeBytes: (_l = upload === null || upload === void 0 ? void 0 : upload.info) === null || _l === void 0 ? void 0 : _l.storage.fileSizeBytes,
                    contentType: (_m = upload === null || upload === void 0 ? void 0 : upload.info) === null || _m === void 0 ? void 0 : _m.storage.contentType,
                    contentEncoding: (_o = upload === null || upload === void 0 ? void 0 : upload.info) === null || _o === void 0 ? void 0 : _o.storage.contentEncoding,
                    contentDisposition: (_p = upload === null || upload === void 0 ? void 0 : upload.info) === null || _p === void 0 ? void 0 : _p.storage.contentDisposition,
                    provider: (_q = upload === null || upload === void 0 ? void 0 : upload.info) === null || _q === void 0 ? void 0 : _q.storage.provider,
                },
                image: (_r = upload === null || upload === void 0 ? void 0 : upload.info) === null || _r === void 0 ? void 0 : _r.image,
                isLogo: (_s = upload === null || upload === void 0 ? void 0 : upload.properties) === null || _s === void 0 ? void 0 : _s.analysisIsLogo,
                isPhoto: (_t = upload === null || upload === void 0 ? void 0 : upload.properties) === null || _t === void 0 ? void 0 : _t.analysisIsPhoto,
                isVector: (_u = upload === null || upload === void 0 ? void 0 : upload.properties) === null || _u === void 0 ? void 0 : _u.analysisIsVector,
            },
            tags: upload.tags,
            _links: upload._links,
        };
    }
    assetFromUpload(upload) {
        return new VistaAsset(this.assetDataFromUpload(upload), AssetType.Original, this.authProvider, undefined, this.callbackFunc);
    }
}
__decorate([
    observable
], VistaAssetStore.prototype, "previousFetchParams", void 0);
__decorate([
    observable
], VistaAssetStore.prototype, "_assets", void 0);
__decorate([
    observable
], VistaAssetStore.prototype, "_fetchStatus", void 0);
__decorate([
    computed
], VistaAssetStore.prototype, "assets", null);
__decorate([
    computed
], VistaAssetStore.prototype, "fetchStatus", null);
__decorate([
    computed
], VistaAssetStore.prototype, "canFetchMore", null);
__decorate([
    action.bound
], VistaAssetStore.prototype, "fetchAssets", null);
__decorate([
    action.bound
], VistaAssetStore.prototype, "setAssets", null);
__decorate([
    action.bound
], VistaAssetStore.prototype, "appendAssets", null);
__decorate([
    action.bound
], VistaAssetStore.prototype, "setFetchStatus", null);
__decorate([
    action.bound
], VistaAssetStore.prototype, "prependAssets", null);
__decorate([
    action.bound
], VistaAssetStore.prototype, "setPreviousFetchParams", null);
__decorate([
    action.bound
], VistaAssetStore.prototype, "fetchMore", null);
//# sourceMappingURL=VistaAssetStore.js.map